import { IComponentOptions, IController, IPromise } from "angular";
import { IRestLayerRequest, RestLayerFilter } from "@gtmhub/core";
import { IModalComponent } from "@gtmhub/core/routing";
import { IIndicator } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { ICollection } from "@webapp/core/core.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ProfileScreenName } from "../../models/models";

export type GroupDetailsSidebarConfig = { label: { icon: string; key: string; infoKey?: string }; group: GroupInfo };
export type GroupType = "metrics" | "goals";
export type GroupInfo = { type: GroupType; icon: string };

export interface IGroupDetailsSidebarComponentBindingsResolve {
  filter: RestLayerFilter;
  config: GroupDetailsSidebarConfig;
  screenName: ProfileScreenName;
}

const FIRST_PAGE = 1;

export type GroupItem = Pick<IGoal | Metric, "id" | "name" | "attainment" | "ownerIds" | "sessionId">;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GroupDetailsSidebarCtrl extends IModalComponent<IGroupDetailsSidebarComponentBindingsResolve> {}
export class GroupDetailsSidebarCtrl implements IController {
  config: GroupDetailsSidebarConfig;
  items: GroupItem[];
  columns: IGHGridColumn[];
  indicators: { loading?: IIndicator } = {};
  currentPage = FIRST_PAGE;
  totalPages: number;
  private limit = 50;
  static $inject = ["GoalService", "MetricService"];

  constructor(private goalService: GoalService, private metricService: MetricService) {}

  $onInit(): void {
    this.config = this.resolve.config;
    this.setItems(this.config.group.type);
    this.columns = [
      {
        fieldName: "name",
        label: "name",
        width: 359,
        visible: true,
      },
      {
        fieldName: "ownerIds",
        label: "owner",
        width: 329,
        visible: true,
      },
      {
        fieldName: "attainment",
        label: "progress",
        width: 100,
        visible: true,
      },
    ];
  }

  onPageChange(page: number): void {
    const newPage = page > this.totalPages || !page ? FIRST_PAGE : page;
    if (this.currentPage !== newPage) {
      this.currentPage = newPage;
      this.setItems(this.config.group.type);
    }
  }

  private setItems(groupType: GroupType): void {
    const skip = (this.currentPage - 1) * this.limit;
    this.indicators.loading = { progress: true };
    const params = {
      filter: this.resolve.filter,
      fields: "id,name,attainment,ownerIds,sessionId",
      limit: this.limit,
      sort: "-attainment",
      skip,
    };

    this.getItemsPromise(groupType, params)
      .then((itemsCollection) => {
        delete this.indicators.loading;
        this.items = itemsCollection.items;
        this.totalPages = Math.ceil(itemsCollection.totalCount / this.limit);
      })
      .catch((error) => (this.indicators.loading = { error }));
  }

  private getItemsPromise(groupType: GroupType, params: IRestLayerRequest): IPromise<ICollection<GroupItem>> {
    const serviceMap: Record<GroupType, (params: IRestLayerRequest) => IPromise<ICollection<GroupItem>>> = {
      metrics: (params) => this.metricService.getMetricsV2(params),
      goals: (params) => this.goalService.getGoalsV2(params),
    };

    return serviceMap[groupType](params);
  }
}

export const GroupDetailsSidebarComponent: IComponentOptions = {
  bindings: {
    dismiss: "&",
    close: "&",
    resolve: "<",
  },
  controller: GroupDetailsSidebarCtrl,
  template: require("./group-details-sidebar.component.html"),
};
